import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Switch,
  FormControlLabel,
  TextField,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tab,
  Tabs,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Notifications,
  Security,
  Language,
  Palette,
  Edit,
  Delete,
  Add,
  Webhook,
  Email,
  Phone,
  ExpandMore,
  Save,
  Restore,
} from '@mui/icons-material';

const Settings = () => {
  const { user } = useSelector((state) => state.auth);
  const [tabValue, setTabValue] = useState(0);
  const [settings, setSettings] = useState({
    notifications: {
      email: true,
      browser: true,
      newLeads: true,
      assignments: true,
      followUps: true,
      callReminders: true,
      dailyDigest: false,
      weeklyReport: true,
    },
    appearance: {
      darkMode: false,
      language: 'en',
      compactView: false,
      sidebarCollapsed: false,
      dateFormat: 'MMM dd, yyyy',
      timeFormat: '24h',
    },
    privacy: {
      showPhone: true,
      showEmail: true,
      allowDirectCalls: true,
      shareActivity: false,
      publicProfile: false,
    },
    system: {
      autoLogout: 480, // minutes
      sessionTimeout: 60,
      enableLogging: true,
      debugMode: false,
    },
  });

  const [webhookDialogOpen, setWebhookDialogOpen] = useState(false);
  const [webhookForm, setWebhookForm] = useState({
    name: '',
    url: '',
    events: [],
  });
  const [webhooks, setWebhooks] = useState([
    {
      id: 1,
      name: 'Google Ads Webhook',
      url: 'https://yourcrm.com/api/webhooks/google/',
      status: 'active',
      events: ['lead_created'],
      lastUsed: '2024-01-15T10:30:00Z',
    },
    {
      id: 2,
      name: 'Meta Lead Ads',
      url: 'https://yourcrm.com/api/webhooks/meta/',
      status: 'active',
      events: ['lead_created', 'lead_updated'],
      lastUsed: '2024-01-15T09:15:00Z',
    },
  ]);

  const handleSettingChange = (category, setting, value) => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [setting]: value,
      },
    }));
  };

  const handleSaveSettings = async () => {
    try {
      // Save settings to backend
      console.log('Saving settings:', settings);
      // Show success message
    } catch (error) {
      console.error('Failed to save settings:', error);
    }
  };

  const handleResetSettings = () => {
    if (window.confirm('Are you sure you want to reset all settings to default?')) {
      // Reset to default settings
      setSettings({
        notifications: {
          email: true,
          browser: true,
          newLeads: true,
          assignments: true,
          followUps: true,
          callReminders: true,
          dailyDigest: false,
          weeklyReport: true,
        },
        appearance: {
          darkMode: false,
          language: 'en',
          compactView: false,
          sidebarCollapsed: false,
          dateFormat: 'MMM dd, yyyy',
          timeFormat: '24h',
        },
        privacy: {
          showPhone: true,
          showEmail: true,
          allowDirectCalls: true,
          shareActivity: false,
          publicProfile: false,
        },
        system: {
          autoLogout: 480,
          sessionTimeout: 60,
          enableLogging: true,
          debugMode: false,
        },
      });
    }
  };

  const handleAddWebhook = () => {
    if (webhookForm.name && webhookForm.url) {
      const newWebhook = {
        id: Date.now(),
        name: webhookForm.name,
        url: webhookForm.url,
        events: webhookForm.events,
        status: 'active',
        lastUsed: null,
      };
      setWebhooks([...webhooks, newWebhook]);
      setWebhookForm({ name: '', url: '', events: [] });
      setWebhookDialogOpen(false);
    }
  };

  const handleDeleteWebhook = (id) => {
    if (window.confirm('Are you sure you want to delete this webhook?')) {
      setWebhooks(webhooks.filter(webhook => webhook.id !== id));
    }
  };

  const isAdmin = user?.role === 'admin';

  const TabPanel = ({ children, value, index, ...other }) => (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <div>
          <Typography variant="h4" gutterBottom>
            Settings
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Configure your preferences and system settings
          </Typography>
        </div>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Restore />}
            onClick={handleResetSettings}
          >
            Reset to Default
          </Button>
          <Button
            variant="contained"
            startIcon={<Save />}
            onClick={handleSaveSettings}
          >
            Save Settings
          </Button>
        </Box>
      </Box>

      <Paper sx={{ width: '100%' }}>
        <Tabs
          value={tabValue}
          onChange={(e, newValue) => setTabValue(newValue)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Notifications" icon={<Notifications />} />
          <Tab label="Appearance" icon={<Palette />} />
          <Tab label="Privacy" icon={<Security />} />
          {isAdmin && <Tab label="System" icon={<Edit />} />}
          {isAdmin && <Tab label="Webhooks" icon={<Webhook />} />}
        </Tabs>

        {/* Notifications Tab */}
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Email Notifications
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemText
                        primary="Email Notifications"
                        secondary="Receive notifications via email"
                      />
                      <ListItemSecondaryAction>
                        <Switch
                          checked={settings.notifications.email}
                          onChange={(e) => handleSettingChange('notifications', 'email', e.target.checked)}
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                    
                    <ListItem>
                      <ListItemText
                        primary="New Lead Alerts"
                        secondary="Get notified when new leads arrive"
                      />
                      <ListItemSecondaryAction>
                        <Switch
                          checked={settings.notifications.newLeads}
                          onChange={(e) => handleSettingChange('notifications', 'newLeads', e.target.checked)}
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                    
                    <ListItem>
                      <ListItemText
                        primary="Assignment Notifications"
                        secondary="Alerts when leads are assigned to you"
                      />
                      <ListItemSecondaryAction>
                        <Switch
                          checked={settings.notifications.assignments}
                          onChange={(e) => handleSettingChange('notifications', 'assignments', e.target.checked)}
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                    
                    <ListItem>
                      <ListItemText
                        primary="Follow-up Reminders"
                        secondary="Reminders for scheduled follow-ups"
                      />
                      <ListItemSecondaryAction>
                        <Switch
                          checked={settings.notifications.followUps}
                          onChange={(e) => handleSettingChange('notifications', 'followUps', e.target.checked)}
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                    
                    <ListItem>
                      <ListItemText
                        primary="Daily Digest"
                        secondary="Daily summary of activities"
                      />
                      <ListItemSecondaryAction>
                        <Switch
                          checked={settings.notifications.dailyDigest}
                          onChange={(e) => handleSettingChange('notifications', 'dailyDigest', e.target.checked)}
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Browser Notifications
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemText
                        primary="Desktop Notifications"
                        secondary="Show desktop notifications"
                      />
                      <ListItemSecondaryAction>
                        <Switch
                          checked={settings.notifications.browser}
                          onChange={(e) => handleSettingChange('notifications', 'browser', e.target.checked)}
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                    
                    <ListItem>
                      <ListItemText
                        primary="Call Reminders"
                        secondary="Notifications for scheduled calls"
                      />
                      <ListItemSecondaryAction>
                        <Switch
                          checked={settings.notifications.callReminders}
                          onChange={(e) => handleSettingChange('notifications', 'callReminders', e.target.checked)}
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                    
                    <ListItem>
                      <ListItemText
                        primary="Weekly Reports"
                        secondary="Weekly performance reports"
                      />
                      <ListItemSecondaryAction>
                        <Switch
                          checked={settings.notifications.weeklyReport}
                          onChange={(e) => handleSettingChange('notifications', 'weeklyReport', e.target.checked)}
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Appearance Tab */}
        <TabPanel value={tabValue} index={1}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Display Settings
                  </Typography>
                  
                  <List>
                    <ListItem>
                      <ListItemText
                        primary="Dark Mode"
                        secondary="Use dark theme"
                      />
                      <ListItemSecondaryAction>
                        <Switch
                          checked={settings.appearance.darkMode}
                          onChange={(e) => handleSettingChange('appearance', 'darkMode', e.target.checked)}
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                    
                    <ListItem>
                      <ListItemText
                        primary="Compact View"
                        secondary="Show more data in less space"
                      />
                      <ListItemSecondaryAction>
                        <Switch
                          checked={settings.appearance.compactView}
                          onChange={(e) => handleSettingChange('appearance', 'compactView', e.target.checked)}
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                    
                    <ListItem>
                      <ListItemText
                        primary="Collapsed Sidebar"
                        secondary="Keep sidebar collapsed by default"
                      />
                      <ListItemSecondaryAction>
                        <Switch
                          checked={settings.appearance.sidebarCollapsed}
                          onChange={(e) => handleSettingChange('appearance', 'sidebarCollapsed', e.target.checked)}
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Localization
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <FormControl fullWidth>
                      <InputLabel>Language</InputLabel>
                      <Select
                        value={settings.appearance.language}
                        onChange={(e) => handleSettingChange('appearance', 'language', e.target.value)}
                        label="Language"
                      >
                        <MenuItem value="en">English</MenuItem>
                        <MenuItem value="hi">हिंदी (Hindi)</MenuItem>
                        <MenuItem value="es">Español</MenuItem>
                        <MenuItem value="fr">Français</MenuItem>
                      </Select>
                    </FormControl>
                    
                    <FormControl fullWidth>
                      <InputLabel>Date Format</InputLabel>
                      <Select
                        value={settings.appearance.dateFormat}
                        onChange={(e) => handleSettingChange('appearance', 'dateFormat', e.target.value)}
                        label="Date Format"
                      >
                        <MenuItem value="MMM dd, yyyy">Jan 15, 2024</MenuItem>
                        <MenuItem value="dd/MM/yyyy">15/01/2024</MenuItem>
                        <MenuItem value="MM/dd/yyyy">01/15/2024</MenuItem>
                        <MenuItem value="yyyy-MM-dd">2024-01-15</MenuItem>
                      </Select>
                    </FormControl>
                    
                    <FormControl fullWidth>
                      <InputLabel>Time Format</InputLabel>
                      <Select
                        value={settings.appearance.timeFormat}
                        onChange={(e) => handleSettingChange('appearance', 'timeFormat', e.target.value)}
                        label="Time Format"
                      >
                        <MenuItem value="12h">12 Hour (2:30 PM)</MenuItem>
                        <MenuItem value="24h">24 Hour (14:30)</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Privacy Tab */}
        <TabPanel value={tabValue} index={2}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Profile Visibility
                  </Typography>
                  
                  <List>
                    <ListItem>
                      <ListItemText
                        primary="Show Phone Number"
                        secondary="Display your phone number to team members"
                      />
                      <ListItemSecondaryAction>
                        <Switch
                          checked={settings.privacy.showPhone}
                          onChange={(e) => handleSettingChange('privacy', 'showPhone', e.target.checked)}
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                    
                    <ListItem>
                      <ListItemText
                        primary="Show Email Address"
                        secondary="Display your email to team members"
                      />
                      <ListItemSecondaryAction>
                        <Switch
                          checked={settings.privacy.showEmail}
                          onChange={(e) => handleSettingChange('privacy', 'showEmail', e.target.checked)}
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                    
                    <ListItem>
                      <ListItemText
                        primary="Public Profile"
                        secondary="Make your profile visible to all users"
                      />
                      <ListItemSecondaryAction>
                        <Switch
                          checked={settings.privacy.publicProfile}
                          onChange={(e) => handleSettingChange('privacy', 'publicProfile', e.target.checked)}
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Communication Preferences
                  </Typography>
                  
                  <List>
                    <ListItem>
                      <ListItemText
                        primary="Allow Direct Calls"
                        secondary="Allow team members to call you directly"
                      />
                      <ListItemSecondaryAction>
                        <Switch
                          checked={settings.privacy.allowDirectCalls}
                          onChange={(e) => handleSettingChange('privacy', 'allowDirectCalls', e.target.checked)}
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                    
                    <ListItem>
                      <ListItemText
                        primary="Share Activity Status"
                        secondary="Let others see when you're online"
                      />
                      <ListItemSecondaryAction>
                        <Switch
                          checked={settings.privacy.shareActivity}
                          onChange={(e) => handleSettingChange('privacy', 'shareActivity', e.target.checked)}
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                  </List>
                  
                  <Divider sx={{ my: 2 }} />
                  
                  <Button variant="outlined" color="error" fullWidth>
                    Change Password
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* System Tab (Admin Only) */}
        {isAdmin && (
          <TabPanel value={tabValue} index={3}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Session Management
                    </Typography>
                    
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <TextField
                        label="Auto Logout (minutes)"
                        type="number"
                        value={settings.system.autoLogout}
                        onChange={(e) => handleSettingChange('system', 'autoLogout', parseInt(e.target.value))}
                        fullWidth
                        helperText="Automatically log out users after inactivity"
                      />
                      
                      <TextField
                        label="Session Timeout (minutes)"
                        type="number"
                        value={settings.system.sessionTimeout}
                        onChange={(e) => handleSettingChange('system', 'sessionTimeout', parseInt(e.target.value))}
                        fullWidth
                        helperText="Session timeout for API requests"
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      System Options
                    </Typography>
                    
                    <List>
                      <ListItem>
                        <ListItemText
                          primary="Enable Logging"
                          secondary="Log system activities and errors"
                        />
                        <ListItemSecondaryAction>
                          <Switch
                            checked={settings.system.enableLogging}
                            onChange={(e) => handleSettingChange('system', 'enableLogging', e.target.checked)}
                          />
                        </ListItemSecondaryAction>
                      </ListItem>
                      
                      <ListItem>
                        <ListItemText
                          primary="Debug Mode"
                          secondary="Enable detailed error messages (development only)"
                        />
                        <ListItemSecondaryAction>
                          <Switch
                            checked={settings.system.debugMode}
                            onChange={(e) => handleSettingChange('system', 'debugMode', e.target.checked)}
                          />
                        </ListItemSecondaryAction>
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>
        )}

        {/* Webhooks Tab (Admin Only) */}
        {isAdmin && (
          <TabPanel value={tabValue} index={4}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">
                    Webhook Management
                  </Typography>
                  <Button
                    startIcon={<Add />}
                    onClick={() => setWebhookDialogOpen(true)}
                    variant="contained"
                  >
                    Add Webhook
                  </Button>
                </Box>
                
                <Alert severity="info" sx={{ mb: 2 }}>
                  Webhooks allow external systems to send lead data to your CRM automatically.
                  Make sure to verify webhook URLs before adding them.
                </Alert>
                
                <List>
                  {webhooks.map((webhook) => (
                    <ListItem key={webhook.id} divider>
                      <ListItemText
                        primary={webhook.name}
                        secondary={
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              {webhook.url}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                              <Chip
                                label={webhook.status}
                                size="small"
                                color={webhook.status === 'active' ? 'success' : 'default'}
                                variant="outlined"
                              />
                              <Typography variant="caption" color="text.secondary">
                                Events: {webhook.events.join(', ')}
                              </Typography>
                              {webhook.lastUsed && (
                                <Typography variant="caption" color="text.secondary">
                                  Last used: {new Date(webhook.lastUsed).toLocaleDateString()}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        }
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          onClick={() => handleDeleteWebhook(webhook.id)}
                          color="error"
                          size="small"
                        >
                          <Delete />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </TabPanel>
        )}
      </Paper>

      {/* Add Webhook Dialog */}
      <Dialog 
        open={webhookDialogOpen} 
        onClose={() => setWebhookDialogOpen(false)}
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>Add New Webhook</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Webhook Name"
              value={webhookForm.name}
              onChange={(e) => setWebhookForm({ ...webhookForm, name: e.target.value })}
              fullWidth
              placeholder="e.g., Google Ads Integration"
            />
            
            <TextField
              label="Webhook URL"
              value={webhookForm.url}
              onChange={(e) => setWebhookForm({ ...webhookForm, url: e.target.value })}
              fullWidth
              placeholder="https://example.com/webhook"
            />
            
            <FormControl fullWidth>
              <InputLabel>Events</InputLabel>
              <Select
                multiple
                value={webhookForm.events}
                onChange={(e) => setWebhookForm({ ...webhookForm, events: e.target.value })}
                label="Events"
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value} size="small" />
                    ))}
                  </Box>
                )}
              >
                <MenuItem value="lead_created">Lead Created</MenuItem>
                <MenuItem value="lead_updated">Lead Updated</MenuItem>
                <MenuItem value="lead_assigned">Lead Assigned</MenuItem>
                <MenuItem value="lead_status_changed">Lead Status Changed</MenuItem>
              </Select>
            </FormControl>
          </Box>
          
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Security Note:</strong> Only add webhook URLs from trusted sources.
              Malicious webhooks can compromise your system security.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWebhookDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleAddWebhook}
            variant="contained"
            disabled={!webhookForm.name || !webhookForm.url}
          >
            Add Webhook
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Settings;